// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';

import { CSSAlignItemsType } from '../../helpers/constants/cssPropTypes';
import useMutationObserver from '../../hooks/useMutationObserver';
import Icon from '../Icon/Icon';
import Heading from '../Typography/Heading/Heading';
import getGlobalProps from '../../helpers/constants/global-props';

import {
  AccordionClickHandler,
  AccordionContentSpacer,
  AccordionContentWrapper,
  AccordionDescriptionWrapper,
  AccordionHeader,
  AccordionInfo,
  AccordionTitle,
  AccordionWrapper,
} from './styles';

export interface AccordionProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title' | 'onClick'> {
  /** Unique identifier */
  id: string;
  /** Title shown in Accordion header */
  title: ReactNode;
  /** @ignore */
  fixedTitleWidth?: boolean;
  /** Short description shown in Accordion header */
  description?: ReactNode;
  /** @ignore */
  twiddlePlacement?: 'start' | 'end';
  alwaysShowDescription?: boolean;
  /** Control the expanded/collapsed state of the Accordion */
  open: boolean;
  /** Set the vertical alignment of the Accordion header content */
  headerVerticalAlignment?: Extract<CSSAlignItemsType, 'center'>;
  /** Open/close onClick callback */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** The Accordion content */
  children?: ReactNode;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string;
}

/**
 * This is a rule to avoid people using interactive elements in the Accordion description
 * This is needed so people don't introduce nested interactive elements which is a problem
 * for accessibility. We can later move this rule into an eslint rule instead of this hack.
 */
const AccordionDescription = ({ description }: { description: React.ReactNode }) => {
  const ref = React.useRef(null);

  useMutationObserver({
    ref,
    observerOptions: { childList: true, subtree: true },
    callback: current => {
      const interactiveChildren = current.querySelectorAll('a,button,input');

      if (interactiveChildren.length) {
        console.group(
          '[_ink] Nested interactive elements are not supported. Remove the following element(s) from the Accordion description:',
        );
        interactiveChildren.forEach(element => console.error(element));
        console.groupEnd();
        throw new Error('[_ink] Nested interactive elements are not supported. See the console for more details.');
      }
    },
  });
  return <AccordionDescriptionWrapper ref={ref}>{description}</AccordionDescriptionWrapper>;
};

/**
 * @purpose A progressive disclosure pattern
 * @group Accordion
 * @categories layout
 * @status beta
 * @aka drawer, progressive disclosure, expand, twiddle, collapsible section, show/hide content
 */
function Accordion(props: AccordionProps) {
  const {
    children,
    className,
    description,
    twiddlePlacement = 'end',
    alwaysShowDescription = false,
    id,
    onClick,
    open,
    style,
    title,
    fixedTitleWidth = true,
    headerVerticalAlignment,
    'data-testid': dataTestId,
    ...otherProps
  } = props;

  const globalProps = getGlobalProps({});

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        fixedTitleWidth,
        accordionOpen: open,
        accordionDescription: !!description,
        accordionTwiddlePlacement: twiddlePlacement,
        accordionAlwaysShowDescription: alwaysShowDescription,
        headerVerticalAlignment,
      })}
    >
      <AccordionWrapper id={id} {...otherProps} {...globalProps} data-testid={dataTestId}>
        <AccordionHeader>
          <AccordionInfo {...{ headerVerticalAlignment }}>
            <AccordionTitle>
              <Heading variant="heading-3" trim>
                {title}
              </Heading>
            </AccordionTitle>

            {description && (!open || alwaysShowDescription) && <AccordionDescription description={description} />}
          </AccordionInfo>

          <AccordionClickHandler
            title={`${open ? 'Collapse' : 'Expand'} accordion content`}
            aria-label={`${open ? 'Collapse' : 'Expand'} accordion content`}
            aria-expanded={open}
            onClick={onClick}
            data-testid={`${dataTestId}-expand-button`}
          >
            <Icon type="down-bold" />
          </AccordionClickHandler>
        </AccordionHeader>
        <AccordionContentWrapper data-testid={`${dataTestId}-content-wrapper`}>
          <AccordionContentSpacer>{children}</AccordionContentSpacer>
        </AccordionContentWrapper>
      </AccordionWrapper>
    </ThemeProvider>
  );
}

Accordion.displayName = 'Accordion';

export default Accordion;
